import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../Styles/OtpVerification.css';
import logo from '../Images/SAVI AI full.png';
import { FiMail, FiShield, FiArrowLeft, FiRefreshCw } from 'react-icons/fi';

const OtpVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email from registration form data
  const userEmail = location.state?.email || 'your email';
  
  // Create refs for OTP inputs
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
    
    // Focus on first input when component mounts
    otpRefs[0].current?.focus();
  }, []);

  // Timer for resend functionality
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Clear error when user starts typing
    if (error) setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        // If current input is empty, move to previous input
        otpRefs[index - 1].current?.focus();
      } else {
        // Clear current input
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const pastedOtp = text.replace(/\D/g, '').slice(0, 6);
        if (pastedOtp.length === 6) {
          setOtp(pastedOtp.split(''));
          otpRefs[5].current?.focus();
        }
      });
    }
  };

  const validateOtp = () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return false;
    }
    if (!/^\d{6}$/.test(otpString)) {
      setError('OTP should contain only numbers');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateOtp()) {
      // Focus on first empty input
      const firstEmptyIndex = otp.findIndex(digit => !digit);
      if (firstEmptyIndex !== -1) {
        otpRefs[firstEmptyIndex].current?.focus();
      }
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Simulate API call for OTP verification
      const otpString = otp.join('');
      console.log('Verifying OTP:', otpString);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For demo purposes, accept any 6-digit OTP
      // In real implementation, you would verify with your backend
      if (otpString === '123456') {
        // Simulate invalid OTP
        setError('Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpRefs[0].current?.focus();
      } else {
        // Success - navigate to login or dashboard
        navigate('/login', { 
          state: { 
            message: 'Account verified successfully! Please sign in.' 
          } 
        });
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || isResending) return;
    
    setIsResending(true);
    setError('');
    
    try {
      // Simulate API call for resending OTP
      console.log('Resending OTP to:', userEmail);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Reset timer and states
      setResendTimer(60);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      otpRefs[0].current?.focus();
      
      // Show success message (you could use a toast notification here)
      setError(''); // Clear any existing errors
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToRegistration = () => {
    navigate('/register');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="otp-main-container">
      <header className="otp-header">
        <div className="otp-logo">
          <img src={logo} alt="SAVI AI" className="otp-logo-image" />
        </div>
        <button className="otp-back-button" onClick={handleBackToRegistration}>
          <FiArrowLeft /> Back to Registration
        </button>
      </header>
      
      <div className="otp-form-container">
        <div className="otp-verification-container">
          <div className="otp-icon-container">
            <FiShield className="otp-shield-icon" />
          </div>
          
          <h1 className="otp-form-title">
            Verify Your Account
          </h1>
          
          <p className="otp-form-subtitle">
            We've sent a 6-digit verification code to
          </p>
          
          <p className="otp-email-display">
            <FiMail className="otp-email-icon" />
            {userEmail}
          </p>
          
          <p className="otp-instruction">
            Enter the code below to complete your registration
          </p>

          {error && <div className="otp-error-message">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="otp-input-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={otpRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`otp-input ${error ? 'otp-input-error' : ''}`}
                  disabled={isLoading}
                  autoComplete="off"
                />
              ))}
            </div>

            <div className="otp-submit-container">
              <button 
                type="submit" 
                className={`otp-submit-button ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="otp-spinner"></div>
                    Verifying...
                  </>
                ) : (
                  'Verify & Complete Registration'
                )}
              </button>
            </div>
          </form>

          <div className="otp-resend-container">
            <p className="otp-resend-text">
              Didn't receive the code?
            </p>
            
            {canResend ? (
              <button 
                className="otp-resend-button"
                onClick={handleResendOtp}
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <FiRefreshCw className="otp-resend-spinner" />
                    Sending...
                  </>
                ) : (
                  'Resend Code'
                )}
              </button>
            ) : (
              <span className="otp-timer">
                Resend in {formatTime(resendTimer)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;



// import React, { useState, useEffect } from 'react';
// import { useNavigate, useLocation } from 'react-router-dom';
// import '../Styles/Register.css'; // Reusing the same styles
// import { FiMail, FiArrowLeft } from 'react-icons/fi';

// const OtpVerification = () => {
//   const [otp, setOtp] = useState(['', '', '', '', '', '']);
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState(false);
//   const [resendDisabled, setResendDisabled] = useState(false);
//   const [countdown, setCountdown] = useState(30);
//   const [verificationAttempts, setVerificationAttempts] = useState(0);
  
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { email, firstName, lastName } = location.state || {};

//   // Create refs for each OTP input
//   const inputRefs = [
//     React.useRef(null),
//     React.useRef(null),
//     React.useRef(null),
//     React.useRef(null),
//     React.useRef(null),
//     React.useRef(null)
//   ];

//   // Handle OTP input change
//   const handleOtpChange = (index, value) => {
//     // Only allow numbers and empty string
//     if (value === '' || /^[0-9]$/.test(value)) {
//       const newOtp = [...otp];
//       newOtp[index] = value;
//       setOtp(newOtp);
//       setError('');
      
//       // Auto focus to next input if a digit was entered
//       if (value !== '' && index < 5) {
//         inputRefs[index + 1].current.focus();
//       }
      
//       // Auto submit if last digit was entered
//       if (index === 5 && value !== '') {
//         handleSubmit();
//       }
//     }
//   };

//   // Handle backspace key
//   const handleKeyDown = (index, e) => {
//     if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
//       inputRefs[index - 1].current.focus();
//     }
//   };

//   // Handle paste
//   const handlePaste = (e) => {
//     e.preventDefault();
//     const pasteData = e.clipboardData.getData('text/plain').trim();
//     if (/^[0-9]{6}$/.test(pasteData)) {
//       const pasteArray = pasteData.split('');
//       setOtp(pasteArray.slice(0, 6));
//       inputRefs[5].current.focus();
//     }
//   };

//   // Resend OTP
//   const handleResendOtp = async () => {
//     if (resendDisabled) return;
    
//     try {
//       setIsLoading(true);
//       setError('');
      
//       // Here you would call your backend API to resend OTP
//       // Example:
//       // const response = await axios.post('/api/resend-otp', { email });
      
//       // Simulate API call
//       await new Promise(resolve => setTimeout(resolve, 1000));
      
//       setResendDisabled(true);
//       setCountdown(30);
//       setError(''); // Clear any previous errors
//       // Show success message or toast here if needed
//     } catch (err) {
//       setError('Failed to resend OTP. Please try again.');
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Countdown timer for resend OTP
//   useEffect(() => {
//     let timer;
//     if (resendDisabled && countdown > 0) {
//       timer = setTimeout(() => setCountdown(countdown - 1), 1000);
//     } else if (countdown === 0) {
//       setResendDisabled(false);
//     }
//     return () => clearTimeout(timer);
//   }, [countdown, resendDisabled]);

//   // Handle form submission
//   const handleSubmit = async (e) => {
//     if (e) e.preventDefault();
    
//     const otpString = otp.join('');
//     if (otpString.length !== 6) {
//       setError('Please enter a 6-digit OTP');
//       return;
//     }
    
//     try {
//       setIsLoading(true);
//       setError('');
      
//       // Here you would call your backend API to verify OTP
//       // Example:
//       // const response = await axios.post('/api/verify-otp', { email, otp: otpString });
      
//       // Simulate API call with 1 second delay
//       await new Promise(resolve => setTimeout(resolve, 1000));
      
//       // Simulate successful verification
//       setSuccess(true);
//       setVerificationAttempts(0);
      
//       // After showing success message, redirect to login or dashboard
//       setTimeout(() => {
//         navigate('/login', { state: { registrationSuccess: true } });
//       }, 3000);
      
//     } catch (err) {
//       setVerificationAttempts(prev => prev + 1);
//       if (verificationAttempts >= 2) {
//         setError('Too many failed attempts. Please request a new OTP.');
//         setResendDisabled(false);
//         setCountdown(0);
//       } else {
//         setError('Invalid OTP. Please try again.');
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleGoBack = () => {
//     navigate(-1); // Go back to previous page
//   };

//   return (
//     <div className="register-user-main-container">
//       <header className="register-user-header">
//         <div className="register-user-logo">
//           <img src="/path/to/logo.png" alt="SAVI AI" className="register-user-logo-image" />
//         </div>
//         <button className="register-user-signin-button" onClick={() => navigate('/login')}>Sign In</button>
//       </header>
      
//       <div className="register-form-container">
//         <div className="register-user-form-container">
//           <button 
//             onClick={handleGoBack}
//             style={{
//               position: 'absolute',
//               left: '20px',
//               top: '20px',
//               background: 'none',
//               border: 'none',
//               cursor: 'pointer',
//               display: 'flex',
//               alignItems: 'center',
//               color: '#666'
//             }}
//           >
//             <FiArrowLeft style={{ marginRight: '5px' }} />
//             Back
//           </button>
          
//           {success ? (
//             <div style={{ textAlign: 'center' }}>
//               <h1 className="register-user-form-title">
//                 Registration Successful!
//               </h1>
//               <div style={{ 
//                 margin: '30px 0',
//                 padding: '20px',
//                 backgroundColor: '#f0fff0',
//                 borderRadius: '8px',
//                 border: '1px solid #a0d8a0'
//               }}>
//                 <p style={{ fontSize: '16px', color: '#2e7d32' }}>
//                   Your account has been successfully created. You'll be redirected to the login page shortly.
//                 </p>
//               </div>
//               <div style={{ marginTop: '20px' }}>
//                 <p>Welcome to SAVI AI, {firstName}!</p>
//               </div>
//             </div>
//           ) : (
//             <>
//               <h1 className="register-user-form-title">
//                 Verify Your Email
//               </h1>
//               <p className='register-user-form-title-p'>
//                 We've sent a 6-digit verification code to {email}
//               </p>
              
//               {error && <p className="register-user-error-message">{error}</p>}
              
//               <form onSubmit={handleSubmit}>
//                 <div style={{ 
//                   display: 'flex', 
//                   justifyContent: 'center', 
//                   gap: '10px', 
//                   margin: '30px 0'
//                 }}>
//                   {otp.map((digit, index) => (
//                     <input
//                       key={index}
//                       type="text"
//                       maxLength="1"
//                       value={digit}
//                       onChange={(e) => handleOtpChange(index, e.target.value)}
//                       onKeyDown={(e) => handleKeyDown(index, e)}
//                       onPaste={handlePaste}
//                       ref={inputRefs[index]}
//                       style={{
//                         width: '40px',
//                         height: '50px',
//                         textAlign: 'center',
//                         fontSize: '20px',
//                         borderRadius: '8px',
//                         border: error ? '1px solid #e53935' : '1px solid #e0e0e0',
//                         backgroundColor: '#f9f9f9',
//                         outline: 'none'
//                       }}
//                       disabled={isLoading}
//                       autoFocus={index === 0}
//                     />
//                   ))}
//                 </div>
                
//                 <div style={{ textAlign: 'center', marginBottom: '20px' }}>
//                   <p style={{ fontSize: '14px', color: '#666' }}>
//                     Didn't receive the code?{' '}
//                     <button 
//                       type="button"
//                       onClick={handleResendOtp}
//                       disabled={resendDisabled || isLoading}
//                       style={{
//                         background: 'none',
//                         border: 'none',
//                         color: resendDisabled ? '#999' : '#ff6900',
//                         cursor: resendDisabled ? 'not-allowed' : 'pointer',
//                         fontWeight: '600',
//                         padding: '0'
//                       }}
//                     >
//                       {resendDisabled ? `Resend in ${countdown}s` : 'Resend OTP'}
//                     </button>
//                   </p>
//                 </div>
                
//                 <div className="register-user-button-container">
//                   <button 
//                     type="submit" 
//                     className="register-user-submit-button"
//                     disabled={isLoading}
//                     style={{
//                       backgroundColor: isLoading ? '#ccc' : '#FF6B00',
//                       cursor: isLoading ? 'wait' : 'pointer'
//                     }}
//                   >
//                     {isLoading ? (
//                       'Verifying...'
//                     ) : (
//                       <>
//                         Verify
//                         <svg 
//                           xmlns="http://www.w3.org/2000/svg" 
//                           width="20" 
//                           height="20" 
//                           viewBox="0 0 24 24" 
//                           fill="none" 
//                           stroke="currentColor" 
//                           strokeWidth="2" 
//                           strokeLinecap="round" 
//                           strokeLinejoin="round"
//                           style={{ marginLeft: '10px' }}
//                         >
//                           <path d="M5 12h14M12 5l7 7-7 7"/>
//                         </svg>
//                       </>
//                     )}
//                   </button>
//                 </div>
//               </form>
//             </>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OtpVerification;